/**
 * i18n — translation layer.
 *
 * Two parallel APIs during migration:
 *   1. Legacy flat-dict (backward compatible):
 *        const { t } = useTranslation();
 *        <h1>{t('home.title')}</h1>
 *   2. Lingui macro (preferred for new code):
 *        import { Trans } from '@lingui/react/macro';
 *        <h1><Trans>Inicio</Trans></h1>
 *
 * Both are fed from the same `dict` below. New <Trans> calls without a
 * translation fall back to the source Spanish text automatically.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * SCOPE RULE — what gets translated and what does NOT:
 *
 *   ✅ DO translate: UI chrome — buttons, labels, modals, navigation,
 *      settings, feedback banners, tooltips, aria-labels, hardcoded copy.
 *
 *   ❌ DO NOT translate: exam content from Firestore — question statements,
 *      answer options, explanations, domains, references, exam-set titles,
 *      or any field sourced from the database. These are data, not UI,
 *      and must be rendered verbatim in their original language.
 *
 *   Practical test: if the string comes from a literal in JSX, it gets
 *   wrapped in <Trans>. If it comes from a variable/prop/fetch, leave it.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * See memory/research/2026-04-16-react-i18n-strategy.md for full plan.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { i18n } from '@lingui/core';

// Each locale's compiled Lingui catalog (~1 chunk per language) is loaded
// on demand instead of all 8 upfront — only the active language's strings
// ship in the initial bundle. Unknown/not-yet-loaded msgids fall back to
// the source Spanish text (see note below), so there's no flash of missing
// copy while a catalog is still in flight.
const localeCatalogLoaders = import.meta.glob('../locales/*/messages.mjs');

const STORAGE_KEY = 'certzen:lang';

export const SUPPORTED_LANGS = [
  { id: 'es', label: 'Español' },
  { id: 'en', label: 'English (USA)' },
  { id: 'fr', label: 'Français' },
  { id: 'pt', label: 'Português' },
  { id: 'de', label: 'Deutsch' },
  { id: 'it', label: 'Italiano' },
  { id: 'zh', label: '中文' },
  { id: 'ja', label: '日本語' },
];

const dict = {
  es: {
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.edit': 'Editar',
    'common.delete': 'Eliminar',
    'common.loading': 'Cargando…',
    'common.back': 'Volver',
    'common.logout': 'Cerrar sesión',

    'nav.home': 'Inicio',
    'nav.library': 'Tu biblioteca',
    'nav.explore': 'Explorar',
    'nav.mySets': 'Mis sets',
    'nav.create': 'Crear examen',
    'nav.folders': 'Tus carpetas',
    'nav.newFolder': 'Nueva carpeta',
    'nav.settings': 'Ajustes',
    'nav.profile': 'Perfil',
    'nav.achievements': 'Logros',
    'nav.pricing': 'Planes',

    'home.greeting.morning': 'Buenos días',
    'home.greeting.afternoon': 'Buenas tardes',
    'home.greeting.evening': 'Buenas noches',
    'home.recent': 'Tu actividad reciente',
    'home.empty': 'Aún no has estudiado. Explora exámenes para empezar.',
    'home.favorites': 'Tus favoritos',
    'home.continue': 'Continuar estudiando',
    'home.recommended': 'Recomendados para ti',

    'settings.title': 'Ajustes',
    'settings.subscription': 'Suscripción',
    'settings.personalInfo': 'Información personal',
    'settings.appearance': 'Apariencia',
    'settings.mode': 'Modo',
    'settings.language': 'Idioma',
    'settings.notifications': 'Notificaciones',
    'settings.account': 'Cuenta y privacidad',
    'settings.changePassword': 'Cambiar la contraseña',
    'settings.deleteAccount': 'Elimina tu cuenta',
    'settings.deleteAccountHelp': 'Esta acción borrará todos tus datos y no puede deshacerse.',
    'settings.mode.light': 'Claro',
    'settings.mode.dark': 'Oscuro',
    'settings.mode.auto': 'Auto',

    'folders.create': 'Crear carpeta',
    'folders.name': 'Nombre',
    'folders.empty': 'Aún no tienes carpetas. Crea una para organizar tus sets.',
  },
  en: {
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.loading': 'Loading…',
    'common.back': 'Back',
    'common.logout': 'Log out',

    'nav.home': 'Home',
    'nav.library': 'Your library',
    'nav.explore': 'Explore',
    'nav.mySets': 'My sets',
    'nav.create': 'Create exam',
    'nav.folders': 'Your folders',
    'nav.newFolder': 'New folder',
    'nav.settings': 'Settings',
    'nav.profile': 'Profile',
    'nav.achievements': 'Achievements',
    'nav.pricing': 'Pricing',

    'home.greeting.morning': 'Good morning',
    'home.greeting.afternoon': 'Good afternoon',
    'home.greeting.evening': 'Good evening',
    'home.recent': 'Your recent activity',
    'home.empty': 'You haven\'t studied yet. Explore exams to get started.',
    'home.favorites': 'Your favorites',
    'home.continue': 'Continue studying',
    'home.recommended': 'Recommended for you',

    'settings.title': 'Settings',
    'settings.subscription': 'Subscription',
    'settings.personalInfo': 'Personal information',
    'settings.appearance': 'Appearance',
    'settings.mode': 'Mode',
    'settings.language': 'Language',
    'settings.notifications': 'Notifications',
    'settings.account': 'Account and privacy',
    'settings.changePassword': 'Change password',
    'settings.deleteAccount': 'Delete your account',
    'settings.deleteAccountHelp': 'This action deletes all your data and cannot be undone.',
    'settings.mode.light': 'Light',
    'settings.mode.dark': 'Dark',
    'settings.mode.auto': 'Auto',

    'folders.create': 'Create folder',
    'folders.name': 'Name',
    'folders.empty': 'You don\'t have any folders yet. Create one to organize your sets.',
  },
  fr: {
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.edit': 'Modifier',
    'common.delete': 'Supprimer',
    'common.loading': 'Chargement…',
    'common.back': 'Retour',
    'common.logout': 'Se déconnecter',

    'nav.home': 'Accueil',
    'nav.library': 'Votre bibliothèque',
    'nav.explore': 'Explorer',
    'nav.mySets': 'Mes sets',
    'nav.create': 'Créer un examen',
    'nav.folders': 'Vos dossiers',
    'nav.newFolder': 'Nouveau dossier',
    'nav.settings': 'Paramètres',
    'nav.profile': 'Profil',
    'nav.achievements': 'Réussites',
    'nav.pricing': 'Tarifs',

    'home.greeting.morning': 'Bonjour',
    'home.greeting.afternoon': 'Bon après-midi',
    'home.greeting.evening': 'Bonsoir',
    'home.recent': 'Votre activité récente',
    'home.empty': 'Vous n\'avez pas encore étudié. Explorez les examens pour commencer.',
    'home.favorites': 'Vos favoris',
    'home.continue': 'Continuer à étudier',
    'home.recommended': 'Recommandé pour vous',

    'settings.title': 'Paramètres',
    'settings.subscription': 'Abonnement',
    'settings.personalInfo': 'Informations personnelles',
    'settings.appearance': 'Apparence',
    'settings.mode': 'Mode',
    'settings.language': 'Langue',
    'settings.notifications': 'Notifications',
    'settings.account': 'Compte et confidentialité',
    'settings.changePassword': 'Changer le mot de passe',
    'settings.deleteAccount': 'Supprimer votre compte',
    'settings.deleteAccountHelp': 'Cette action supprime toutes vos données et est irréversible.',
    'settings.mode.light': 'Clair',
    'settings.mode.dark': 'Sombre',
    'settings.mode.auto': 'Auto',

    'folders.create': 'Créer un dossier',
    'folders.name': 'Nom',
    'folders.empty': 'Vous n\'avez pas encore de dossiers. Créez-en un pour organiser vos sets.',
  },
  pt: {
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.edit': 'Editar',
    'common.delete': 'Eliminar',
    'common.loading': 'A carregar…',
    'common.back': 'Voltar',
    'common.logout': 'Terminar sessão',

    'nav.home': 'Início',
    'nav.library': 'A tua biblioteca',
    'nav.explore': 'Explorar',
    'nav.mySets': 'Meus sets',
    'nav.create': 'Criar exame',
    'nav.folders': 'As tuas pastas',
    'nav.newFolder': 'Nova pasta',
    'nav.settings': 'Definições',
    'nav.profile': 'Perfil',
    'nav.achievements': 'Conquistas',
    'nav.pricing': 'Planos',

    'home.greeting.morning': 'Bom dia',
    'home.greeting.afternoon': 'Boa tarde',
    'home.greeting.evening': 'Boa noite',
    'home.recent': 'A tua atividade recente',
    'home.empty': 'Ainda não estudaste. Explora os exames para começar.',
    'home.favorites': 'Os teus favoritos',
    'home.continue': 'Continuar a estudar',
    'home.recommended': 'Recomendado para ti',

    'settings.title': 'Definições',
    'settings.subscription': 'Subscrição',
    'settings.personalInfo': 'Informações pessoais',
    'settings.appearance': 'Aparência',
    'settings.mode': 'Modo',
    'settings.language': 'Idioma',
    'settings.notifications': 'Notificações',
    'settings.account': 'Conta e privacidade',
    'settings.changePassword': 'Alterar palavra-passe',
    'settings.deleteAccount': 'Eliminar a tua conta',
    'settings.deleteAccountHelp': 'Esta ação apaga todos os teus dados e é irreversível.',
    'settings.mode.light': 'Claro',
    'settings.mode.dark': 'Escuro',
    'settings.mode.auto': 'Auto',

    'folders.create': 'Criar pasta',
    'folders.name': 'Nome',
    'folders.empty': 'Ainda não tens pastas. Cria uma para organizar os teus sets.',
  },
  de: {
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.edit': 'Bearbeiten',
    'common.delete': 'Löschen',
    'common.loading': 'Lädt…',
    'common.back': 'Zurück',
    'common.logout': 'Abmelden',

    'nav.home': 'Startseite',
    'nav.library': 'Deine Bibliothek',
    'nav.explore': 'Entdecken',
    'nav.mySets': 'Meine Sets',
    'nav.create': 'Prüfung erstellen',
    'nav.folders': 'Deine Ordner',
    'nav.newFolder': 'Neuer Ordner',
    'nav.settings': 'Einstellungen',
    'nav.profile': 'Profil',
    'nav.achievements': 'Erfolge',
    'nav.pricing': 'Preise',

    'home.greeting.morning': 'Guten Morgen',
    'home.greeting.afternoon': 'Guten Tag',
    'home.greeting.evening': 'Guten Abend',
    'home.recent': 'Deine letzte Aktivität',
    'home.empty': 'Du hast noch nicht gelernt. Entdecke Prüfungen, um zu beginnen.',
    'home.favorites': 'Deine Favoriten',
    'home.continue': 'Weiter lernen',
    'home.recommended': 'Für dich empfohlen',

    'settings.title': 'Einstellungen',
    'settings.subscription': 'Abonnement',
    'settings.personalInfo': 'Persönliche Daten',
    'settings.appearance': 'Darstellung',
    'settings.mode': 'Modus',
    'settings.language': 'Sprache',
    'settings.notifications': 'Benachrichtigungen',
    'settings.account': 'Konto und Datenschutz',
    'settings.changePassword': 'Passwort ändern',
    'settings.deleteAccount': 'Konto löschen',
    'settings.deleteAccountHelp': 'Diese Aktion löscht alle deine Daten und ist unumkehrbar.',
    'settings.mode.light': 'Hell',
    'settings.mode.dark': 'Dunkel',
    'settings.mode.auto': 'Auto',

    'folders.create': 'Ordner erstellen',
    'folders.name': 'Name',
    'folders.empty': 'Du hast noch keine Ordner. Erstelle einen, um deine Sets zu organisieren.',
  },
  it: {
    'common.save': 'Salva',
    'common.cancel': 'Annulla',
    'common.edit': 'Modifica',
    'common.delete': 'Elimina',
    'common.loading': 'Caricamento…',
    'common.back': 'Indietro',
    'common.logout': 'Disconnetti',

    'nav.home': 'Home',
    'nav.library': 'La tua libreria',
    'nav.explore': 'Esplora',
    'nav.mySets': 'I miei set',
    'nav.create': 'Crea esame',
    'nav.folders': 'Le tue cartelle',
    'nav.newFolder': 'Nuova cartella',
    'nav.settings': 'Impostazioni',
    'nav.profile': 'Profilo',
    'nav.achievements': 'Obiettivi',
    'nav.pricing': 'Piani',

    'home.greeting.morning': 'Buongiorno',
    'home.greeting.afternoon': 'Buon pomeriggio',
    'home.greeting.evening': 'Buonasera',
    'home.recent': 'La tua attività recente',
    'home.empty': 'Non hai ancora studiato. Esplora gli esami per iniziare.',
    'home.favorites': 'I tuoi preferiti',
    'home.continue': 'Continua a studiare',
    'home.recommended': 'Consigliati per te',

    'settings.title': 'Impostazioni',
    'settings.subscription': 'Abbonamento',
    'settings.personalInfo': 'Informazioni personali',
    'settings.appearance': 'Aspetto',
    'settings.mode': 'Modalità',
    'settings.language': 'Lingua',
    'settings.notifications': 'Notifiche',
    'settings.account': 'Account e privacy',
    'settings.changePassword': 'Cambia password',
    'settings.deleteAccount': 'Elimina il tuo account',
    'settings.deleteAccountHelp': 'Questa azione cancella tutti i tuoi dati e non può essere annullata.',
    'settings.mode.light': 'Chiaro',
    'settings.mode.dark': 'Scuro',
    'settings.mode.auto': 'Auto',

    'folders.create': 'Crea cartella',
    'folders.name': 'Nome',
    'folders.empty': 'Non hai ancora cartelle. Creane una per organizzare i tuoi set.',
  },
  zh: {
    'common.save': '保存',
    'common.cancel': '取消',
    'common.edit': '编辑',
    'common.delete': '删除',
    'common.loading': '加载中…',
    'common.back': '返回',
    'common.logout': '退出登录',

    'nav.home': '首页',
    'nav.library': '你的资料库',
    'nav.explore': '探索',
    'nav.mySets': '我的套题',
    'nav.create': '创建考试',
    'nav.folders': '你的文件夹',
    'nav.newFolder': '新建文件夹',
    'nav.settings': '设置',
    'nav.profile': '个人资料',
    'nav.achievements': '成就',
    'nav.pricing': '价格',

    'home.greeting.morning': '早上好',
    'home.greeting.afternoon': '下午好',
    'home.greeting.evening': '晚上好',
    'home.recent': '你的最近活动',
    'home.empty': '你还没有学习。去探索考试开始吧。',
    'home.favorites': '你的收藏',
    'home.continue': '继续学习',
    'home.recommended': '为你推荐',

    'settings.title': '设置',
    'settings.subscription': '订阅',
    'settings.personalInfo': '个人信息',
    'settings.appearance': '外观',
    'settings.mode': '模式',
    'settings.language': '语言',
    'settings.notifications': '通知',
    'settings.account': '账户与隐私',
    'settings.changePassword': '修改密码',
    'settings.deleteAccount': '删除账户',
    'settings.deleteAccountHelp': '此操作将删除你的所有数据，无法撤销。',
    'settings.mode.light': '浅色',
    'settings.mode.dark': '深色',
    'settings.mode.auto': '自动',

    'folders.create': '创建文件夹',
    'folders.name': '名称',
    'folders.empty': '你还没有文件夹。创建一个来组织你的资料集。',
  },
  ja: {
    'common.save': '保存',
    'common.cancel': 'キャンセル',
    'common.edit': '編集',
    'common.delete': '削除',
    'common.loading': '読み込み中…',
    'common.back': '戻る',
    'common.logout': 'ログアウト',

    'nav.home': 'ホーム',
    'nav.library': 'ライブラリ',
    'nav.explore': '探す',
    'nav.mySets': '自分のセット',
    'nav.create': '試験を作成',
    'nav.folders': 'フォルダ',
    'nav.newFolder': '新しいフォルダ',
    'nav.settings': '設定',
    'nav.profile': 'プロフィール',
    'nav.achievements': '実績',
    'nav.pricing': '料金',

    'home.greeting.morning': 'おはようございます',
    'home.greeting.afternoon': 'こんにちは',
    'home.greeting.evening': 'こんばんは',
    'home.recent': '最近のアクティビティ',
    'home.empty': 'まだ学習していません。試験を探して始めましょう。',
    'home.favorites': 'お気に入り',
    'home.continue': '学習を続ける',
    'home.recommended': 'おすすめ',

    'settings.title': '設定',
    'settings.subscription': 'サブスクリプション',
    'settings.personalInfo': '個人情報',
    'settings.appearance': '外観',
    'settings.mode': 'モード',
    'settings.language': '言語',
    'settings.notifications': '通知',
    'settings.account': 'アカウントとプライバシー',
    'settings.changePassword': 'パスワードを変更',
    'settings.deleteAccount': 'アカウントを削除',
    'settings.deleteAccountHelp': 'この操作はすべてのデータを削除し、取り消せません。',
    'settings.mode.light': 'ライト',
    'settings.mode.dark': 'ダーク',
    'settings.mode.auto': '自動',

    'folders.create': 'フォルダを作成',
    'folders.name': '名前',
    'folders.empty': 'まだフォルダがありません。セットを整理するために作成しましょう。',
  },
};

const VALID_LANGS = new Set(SUPPORTED_LANGS.map((l) => l.id));

export const useLangStore = create(
  persist(
    (set) => ({
      lang: 'es',
      setLang: (lang) => set({ lang: VALID_LANGS.has(lang) ? lang : 'es' }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (s) => ({ lang: s.lang }),
      // On rehydration, reset invalid languages saved from a previous version.
      onRehydrateStorage: () => (state) => {
        if (state && !VALID_LANGS.has(state.lang)) {
          state.lang = 'es';
        }
      },
    },
  ),
);

export function useTranslation() {
  const { lang, setLang } = useLangStore();
  const t = (key) => dict[lang]?.[key] ?? dict.en?.[key] ?? dict.es[key] ?? key;
  return { t, lang, setLang };
}

// ---------------------------------------------------------------------------
// Lingui runtime initialization
//
// Each locale gets TWO layers of messages merged together:
//   1. Legacy flat dict (keys like 'nav.home') — for callers that still use
//      useTranslation().t('nav.home').
//   2. Content-based msgids from src/locales/messages.js — produced by the
//      @lingui/babel-plugin-lingui-macro from <Trans> macros. The Spanish
//      source text IS the msgid, so translations live keyed by that text.
//
// Unknown msgids fall back to the source Spanish, which keeps the UI legible
// while the migration is in progress — including the brief moment before a
// locale's catalog has finished loading.
// ---------------------------------------------------------------------------
const loadedLocales = new Set();

async function ensureLocaleLoaded(id) {
  if (loadedLocales.has(id) || !VALID_LANGS.has(id)) return;
  const loader = localeCatalogLoaders[`../locales/${id}/messages.mjs`];
  if (!loader) return;
  const mod = await loader();
  i18n.load(id, mod.messages ?? {});
  loadedLocales.add(id);
}

const initialLang = useLangStore.getState().lang;
i18n.activate(initialLang); // activates immediately; catalog fills in once loaded below
ensureLocaleLoaded(initialLang);

// Keep Lingui in sync with the Zustand store.
useLangStore.subscribe((state) => {
  if (!VALID_LANGS.has(state.lang)) return;
  ensureLocaleLoaded(state.lang).then(() => i18n.activate(state.lang));
});

export { i18n };
